// src/api/whatsappApi.ts
// Adaptador de WhatsApp del DUEÑO contra lealify-base (Inc11b + Bloque C). Reemplaza
// wahaService (proxy PHP + Firebase), whatsappSessionService, whatsappDisclaimerService y
// whatsappRequestService. El backend es el broker: el dueño conecta/desconecta SU sesión
// (la configurada del tenant, server-side), acepta el descargo, solicita activación
// ("bajo solicitud"), elige modo auto/manual y envía 1-a-1 a clientes (server-side).
// Sin WhatsApp aprovisionado → {configured:false} y el panel muestra "solicitar WhatsApp".
import { baseApi } from './baseClient';

export interface WhatsAppMe {
  id?: string;
  pushName?: string;
}

export interface WhatsAppDisclaimerState {
  accepted: boolean;
  acceptedAt?: string | null;
  version?: string;
}

export interface WhatsAppStatus {
  configured: boolean;
  connected?: boolean;
  // WORKING | SCAN_QR_CODE | STARTING | STOPPED | STOPPING | FAILED | ERROR | UNKNOWN | HTTP nnn
  status?: string;
  me?: WhatsAppMe | null;
  qr?: string | null;   // data-uri PNG, solo en qr()
  retry?: boolean;      // la sesión aún madura; el panel debe reintentar
  detail?: string;
  // Bloque C — enriquecido en /messages/whatsapp-status:
  requested?: boolean;                       // solicitud "bajo solicitud" viva (pending/provisioning)
  disclaimer?: WhatsAppDisclaimerState | null;
}

export type WhatsAppSendMode = 'auto' | 'manual';

// ── Pausa de mensajes automáticos (F3, 9-sep-2026) ─────────────────────────
// Backend: lealify-base `lib/waPause.js`. Nivel 1 = campañas y masivos ·
// Nivel 2 = todo lo automático (puntos de lealtad, cumpleaños, gift cards,
// referidos, recibos). Lo que manda una PERSONA desde el panel no se pausa
// nunca: el freno es para lo que sale solo, no para la operación. Nació del
// caso Smilix (8-sep): sin esto, el único freno del cliente es desvincular su
// WhatsApp, que apaga también lo que sí quería que saliera.
export interface WaPauseState {
  activa: boolean;
  level: 0 | 1 | 2;
  until: string | null;
  by?: string | null;
  reason?: string | null;
}

// ── Salud del número (foto diaria del cron wa-health, lealify-base) ─────────
export interface WaHealthComponent {
  max?: number;
  pts?: number | null;
  rate?: number | null;
  medidos?: number;
  respondieron?: number;
  via_reaccion?: number;
  proactivos_30d?: number;
  sin_respuesta_est?: number | null;
  dias?: number | null;
  envios_14d?: number;
  explica?: string;   // frase con los números reales del componente (latest() la genera al leer)
  fallos_14d?: number;
  // Motor v2 (26-jul-2026): métricas desde wa_inbound/ledger propio
  vivos?: number;
  contrapartes?: number;
  con_fuente?: boolean;
  n?: number;
  nuevos?: number;
  frios?: number;
  c30?: number;
  last?: string | null;
}

/** Estado del gate anti-ban (F3): restricción vigente y qué pospuso en 7 días. */
export interface WaGateStatus {
  activo: boolean;
  restriccion?: 'ninguna' | 'frio' | 'todo';
  motivo?: string | null;
  label?: string | null;
  hasta?: string | null;
  rechazos?: Array<{ motivo: string; label: string; n: number; numeros: number; ultima: string }>;
}

/**
 * Serie diaria del score para la gráfica de tendencia. El backend la manda SIEMPRE
 * (routes/messages.js), aunque el front lleve tiempo sin pintarla; misma forma que
 * `HistoriaSalud` de SaludTendencia.tsx — se declara aquí para no hacer que la capa
 * `api/` dependa de un componente.
 */
export interface WaHealthHistoria {
  puntos: Array<{ dia: string; score: number; semaforo?: string; session_status?: string }>;
  dias_con_datos: number;
  delta7: number | null;
  desde: string | null;
  truncada_por_motor?: string;
}

export interface WaHealth {
  available: boolean;
  score?: number;
  semaforo?: 'verde' | 'ambar' | 'rojo';
  veredicto?: string;
  session_status?: string;
  parts?: Record<string, WaHealthComponent> & { overrides?: any } | null;
  measured_at?: string;
  stale?: boolean;
  gate?: WaGateStatus | null;
  historia?: WaHealthHistoria | null;
}

export interface ManualSendResult {
  ok: boolean;
  status: 'sent' | 'no_whatsapp' | 'failed';
}

export const whatsappApi = {
  // Estado para el badge (verde/rojo) + requested/disclaimer (alimenta whatsappAccess y el modal).
  status: () => baseApi.get<WhatsAppStatus>('/messages/whatsapp-status'),
  // QR de vinculación (o {connected:true} si ya enlazado).
  qr: () => baseApi.get<WhatsAppStatus>('/messages/whatsapp/qr'),
  // Salud del número (score 0-100 + semáforo; foto del cron diario).
  health: () => baseApi.get<WaHealth>('/messages/whatsapp/health'),
  start: () => baseApi.post<WhatsAppStatus>('/messages/whatsapp/start'),
  restart: () => baseApi.post<WhatsAppStatus>('/messages/whatsapp/restart'),
  logout: () => baseApi.post<WhatsAppStatus>('/messages/whatsapp/logout'),

  // ── Bloque C ──────────────────────────────────────────────────────────────
  // Descargo de responsabilidad (reemplaza users/{uid}.whatsappDisclaimer).
  acceptDisclaimer: (version = 'v1') =>
    baseApi.post<{ ok: boolean; disclaimer: WhatsAppDisclaimerState }>('/messages/whatsapp/disclaimer', { version }),

  // WhatsApp bajo solicitud (reemplaza la colección whatsappRequests + watcher server4).
  requestActivation: (phone?: string | null) =>
    baseApi.post<{ ok: boolean; requested: boolean }>('/messages/whatsapp/request', { phone: phone || null }),

  // Modo de envío auto/manual (reemplaza businesses.whatsappSendMode).
  getSendMode: () => baseApi.get<{ mode: WhatsAppSendMode }>('/messages/send-mode'),
  setSendMode: (mode: WhatsAppSendMode) => baseApi.put<{ mode: WhatsAppSendMode }>('/messages/send-mode', { mode }),

  // Envío manual 1-a-1 server-side (botón "Contactar"). register:true → solo registra (modo wa.me).
  sendManual: (payload: {
    ref_key: string;
    phone?: string;
    message?: string;
    image?: string | null;
    contact_id?: string | number | null;
    register?: boolean;
  }) => baseApi.post<ManualSendResult>('/messages/send', payload),

  // Estados de envío por lote (para pintar "Contactado" al cargar la lista).
  manualStatuses: (refs: string[]) =>
    baseApi.post<{ statuses: Record<string, 'sent' | 'no_whatsapp' | 'failed'> }>('/messages/manual-status', { refs }),

  // Mensaje de PRUEBA a un número (confirma que la sesión funciona). No registra ni descuenta cap.
  sendTest: (phone: string, message?: string) =>
    baseApi.post<{ ok: boolean; shadow?: boolean; detail?: string | null }>('/messages/whatsapp/test', { phone, message }),

  // Cupo de mensajes de la hora (cap de campañas: X de 30 restantes).
  quota: () => baseApi.get<{ cap: number; used: number; remaining: number }>('/messages/whatsapp/quota'),

  // Pausa de mensajes automáticos. `minutes` lo acota el backend a 1..1440: la
  // caducidad es obligatoria porque una pausa olvidada se come mensajes de verdad.
  getPause: () => baseApi.get<WaPauseState>('/messages/wa-pause'),
  setPause: (level: 1 | 2, minutes: number) =>
    baseApi.put<WaPauseState>('/messages/wa-pause', { level, minutes }),
  clearPause: () => baseApi.del<WaPauseState>('/messages/wa-pause'),
};

export default whatsappApi;
